// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as sdk from '@canton-network/dapp-sdk';
import { useCallback, useEffect, useState } from 'react';

export const useConnect = () => {
  const [status, setStatus] = useState<sdk.dappAPI.StatusEvent | undefined>(undefined);
  const [providerReady, setProviderReady] = useState(() => !!window.canton);
  const [isLoading, setLoading] = useState(true);

  const connect = useCallback(async () => {
    try {
      setLoading(true);
      const s = await sdk.connect();
      setProviderReady(true);
      setStatus(s);
    } catch (err) {
      console.error('Error connecting to wallet:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      setLoading(true);
      await sdk.disconnect();
      setStatus(undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!providerReady && !window.canton) {
      setLoading(false);
      return () => {};
    }
    let isActive = true;

    const onStatusChanged = (s: sdk.dappAPI.StatusEvent) => {
      if (!isActive) return;
      if (!s.isConnected) {
        setStatus(undefined);
      } else {
        setStatus(s);
      }
      setLoading(false);
    };

    sdk.onStatusChanged(onStatusChanged).catch((err) => {
      console.error('Failed to subscribe to status changes', err);
    });

    sdk
      .status()
      .then((s) => {
        if (isActive) {
          setStatus(s);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch initial status', err);
        if (isActive) {
          setStatus(undefined);
          setLoading(false);
        }
      });

    return () => {
      isActive = false;
      sdk.removeOnStatusChanged(onStatusChanged).catch(console.error);
    };
  }, [providerReady]);

  return {
    connect,
    disconnect,
    status,
    isLoading,
  };
};
